import type { MapRef } from "@vis.gl/react-maplibre";
import { LngLatBounds } from "maplibre-gl";
import { RefObject, useEffect } from "react";

import { fitMapLibreBoundsSafely, getAliveMap } from "@ryde/controls";
import { TLatLngTuple } from "@ryde/shared";

import {
  MAP_FIT_MAX_ZOOM,
  MAP_FIT_PADDING_PX,
} from "../investigation.constants";

/**
 * Fits the map to the whole ride when the position set changes (on open, then again once the trail
 * loads). MapLibre twin of the Leaflet `FitToRide`, keyed on the count for the same reason: the arrays
 * are rebuilt on every poll, so refitting on identity would fight the user's own panning.
 */
export const useInvestigationMapFit = ({
  mapRef,
  positions,
  isMapLoaded,
}: {
  mapRef: RefObject<MapRef | null>;
  positions: TLatLngTuple[];
  isMapLoaded: boolean;
}): void => {
  const positionCount = positions.length;

  useEffect(() => {
    const map = getAliveMap(mapRef.current);
    if (!map || !isMapLoaded || !positionCount) {
      return;
    }

    let west = Number.POSITIVE_INFINITY;
    let south = Number.POSITIVE_INFINITY;
    let east = Number.NEGATIVE_INFINITY;
    let north = Number.NEGATIVE_INFINITY;

    for (const [lat, lng] of positions) {
      // One bad GPS sample must not poison the bbox: `LngLatBounds.extend(NaN)` makes fitBounds NaN zoom.
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        continue;
      }
      west = Math.min(west, lng);
      east = Math.max(east, lng);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }

    if (!Number.isFinite(west) || !Number.isFinite(south)) {
      return;
    }

    fitMapLibreBoundsSafely({
      map,
      bounds: new LngLatBounds([west, south], [east, north]),
      padding: MAP_FIT_PADDING_PX,
      maxZoom: MAP_FIT_MAX_ZOOM,
    });
    // Refit on count changes only — see the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, isMapLoaded, positionCount]);
};
